import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import authService from "../../services/authService";

import logo from "../../assets/logo.png";

interface ForgotPasswordForm {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ForgotPasswordForm>();
  const email = watch("email");
  const siteName = process.env.REACT_APP_SITE_NAME || "XRP Trading Platform";

  const onSubmit = async (data: ForgotPasswordForm) => {
    try {
      setLoading(true);
      setError(null);
      await authService.forgotPassword(data.email);
      setSuccess(true);
      setEmailSent(data.email);
    } catch (err: any) {
      setError(err.message || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (email) {
      onSubmit({ email });
    }
  };

  return (
    <div className={globalStyle.container}>
      {/* <Header /> */}

      <main className="page-content2">
        <div className="page-content2-bg"></div>
        <div className="page-content2-wrapper">
          <div className="page-content2-content">
            <div className="max-w-md mx-auto py-8 md:py-12">
              <Link to="/" className="text-center mb-8">
                <img
                  src={logo}
                  alt={siteName}
                  className="w-16 h-16 mx-auto mb-4"
                />
                <h2 className={globalStyle.h3}>Forgot Password</h2>
                <p className={globalStyle.body2 + " text-gray-400"}>
                  Enter your email to receive a password reset OTP
                </p>
              </Link>

              <Card padding="lg" className="mt-6">
                {error && (
                  <Alert
                    type="error"
                    message={error}
                    onClose={() => setError(null)}
                    className="mb-6"
                  />
                )}

                {success ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-900 flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-green-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h3 className={globalStyle.h5}>Check Your Email</h3>
                    <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
                      If an account exists for{" "}
                      <strong className="text-white">{emailSent}</strong>, you
                      will receive an OTP to reset your password.
                    </p>
                    <div className="mt-6 space-y-3">
                      <Button
                        onClick={() =>
                          navigate("/verify-password-reset", {
                            state: { email: emailSent },
                          })
                        }
                        fullWidth
                      >
                        Enter OTP
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleResend}
                        loading={loading}
                        fullWidth
                      >
                        Resend OTP
                      </Button>
                      <Link to="/login">
                        <Button variant="tertiary" fullWidth>
                          Back to Login
                        </Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>Email Address</label>
                      <input
                        type="email"
                        className={globalStyle.input}
                        placeholder="Enter your email address"
                        {...register("email", {
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address",
                          },
                        })}
                      />
                      {errors.email && (
                        <p className={globalStyle.formError}>
                          {errors.email.message}
                        </p>
                      )}
                    </div>

                    <Button type="submit" fullWidth loading={loading} size="lg">
                      Send Reset OTP
                    </Button>
                  </form>
                )}

                <div className="mt-8 pt-6 border-t border-gray-800">
                  <p className="text-center text-sm text-gray-400">
                    Remember your password?{" "}
                    <Link
                      to="/login"
                      className="text-primary font-medium hover:text-primary-300"
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              </Card>

              <div className="mt-8 text-center">
                <p className={globalStyle.caption + " text-gray-500"}>
                  For security reasons, the OTP will expire in 10 minutes
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
